import React, { useState, useEffect, useRef, forwardRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Scrollbars } from 'react-custom-scrollbars-2';
import './ChatArtifact.css';
import ChatMessageUser from '../ChatMessageUser/ChatMessageUser';
import ChatMessageAssistant from '../ChatMessageAssistant/ChatMessageAssistant';
import BernieLoading from '../BernieLoading/BernieLoading';
import Sidebar from '../Sidebar/Sidebar';
// React icons
import { HiOutlineBars3 } from 'react-icons/hi2';
import { HiX } from 'react-icons/hi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { LuSendHorizontal } from "react-icons/lu";
// Artifact icons and actions
import { BiNote } from 'react-icons/bi';
import { FiRefreshCw, FiPrinter, FiDownload, FiX, FiMoreVertical, FiLogOut } from 'react-icons/fi';
import ArtifactPanel from '../ArtifactPanel/ArtifactPanel';
import printArtifact from '../ArtifactPanel/PrintDocument';
import generatePdf from '../ArtifactPanel/PdfDocument';
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import ConversationPrint from '../ArtifactPanel/ConversationPrint';
import { printConversation, downloadConversationPdf } from '../ArtifactPanel/PrintUtils';

// Custom scrollbar thumbs and tracks with improved styling
const CustomScrollbars = forwardRef(({ children, ...props }, ref) => {
  return (
    <Scrollbars
      ref={ref}
      renderThumbVertical={({ style, ...props }) => (
        <div
          {...props}
          style={{
            ...style,
            backgroundColor: '#cbd5e1',
            borderRadius: '4px',
            width: '6px',
          }}
          className="thumb-vertical"
        />
      )}
      renderTrackVertical={({ style, ...props }) => (
        <div
          {...props}
          style={{
            ...style,
            right: '2px',
            bottom: '2px',
            top: '2px',
            borderRadius: '4px',
            width: '6px',
          }}
          className="track-vertical"
        />
      )}
      hideTracksWhenNotNeeded={true}
      {...props}
    >
      {children}
    </Scrollbars>
  );
});

// Add display name for debugging
CustomScrollbars.displayName = 'CustomScrollbars';

const ChatLayout = () => {
  const [showSidebar, setShowSidebar] = useState(false); // Sidebar closed by default
  const [showArtifact, setShowArtifact] = useState(false);
  const [chatWidth, setChatWidth] = useState(60); // percentage
  const [messages, setMessages] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState(isMobile ? 40 : 48); // default height in px - shorter for mobile
  const [isDragging, setIsDragging] = useState(false); // Track dragging state
  const [textareaContent, setTextareaContent] = useState(''); // Track textarea content

  // Conversation management state
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editedConversationName, setEditedConversationName] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [page, setPage] = useState(1);
  const [totalConversations, setTotalConversations] = useState(0); // Total conversations in database

  // Artifact state
  const [artifactTitle, setArtifactTitle] = useState("Führungsberatung Dokument");
  const [artifactContent, setArtifactContent] = useState(null);
  const [isArtifactLoading, setIsArtifactLoading] = useState(false);
  const [loadingTime, setLoadingTime] = useState(0);
  const [loadingTimer, setLoadingTimer] = useState(null);
  const artifactRef = useRef(null);

  const dragRef = useRef(null);
  const textareaRef = useRef(null);
  const mainContainerRef = useRef(null);
  const containerRef = useRef(null); // Reference to the container for calculating bounds
  const chatContainerRef = useRef(null); // Reference for chat container to enable auto-scrolling
  const textareaScrollbarRef = useRef(null); // Reference for textarea scrollbar

  const [isLoading, setIsLoading] = useState(false);
  const [typingAnimation, setTypingAnimation] = useState(false);
  const [lastReceivedMessage, setLastReceivedMessage] = useState(null);
  const [typingSpeed, setTypingSpeed] = useState(100);

  // Thinking text state
  const [thinkingText, setThinkingText] = useState("Hmm...");
  const [dotCount, setDotCount] = useState(0);
  const thinkingPhrases = [
    "Hm", "Ja", "Mhh", "Interessant…", "Aha…",
    "Verstehe", "Ah, ich sehe", "Ich verstehe", "Okay",
  ];

  // Add a new state variable for the print panel
  const [showPrintPanel, setShowPrintPanel] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [printContent, setPrintContent] = useState(null);

  // Add a new state variable for the artifact content type
  const [artifactContentType, setArtifactContentType] = useState('summary'); // 'summary' or 'print'

  // Innerhalb des ChatLayout Komponente - füge neue States hinzu
  const [userName, setUserName] = useState('Benutzer');
  const [userInitials, setUserInitials] = useState('B');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  // Fetch conversations
  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      setLoadingConversations(true);
      const response = await axios.post(`/sessions?page=1&per_page=15`);

      setConversations(response.data.conversations?.data || []);

      // Check if there are more pages by comparing current_page with last_page
      const currentPage = response.data.conversations?.current_page || 1;
      const lastPage = response.data.conversations?.last_page || 1;

      // Set total conversation count from API response
      const total = response.data.conversations?.total || 0;
      setTotalConversations(total);

      // Only show "Load More" if we're not on the last page
      setHasMoreConversations(currentPage < lastPage);
      setPage(1);

    } catch (error) {
      // Error handled silently
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadMoreConversations = async () => {
    if (loadingMoreConversations) return;

    try {
      setLoadingMoreConversations(true);
      const nextPage = page + 1;
      const response = await axios.post(`/sessions?page=${nextPage}&per_page=15`);

      // Add new conversations to the existing list
      setConversations(prevConversations => [
        ...prevConversations,
        ...(response.data.conversations?.data || [])
      ]);

      // Check if there are more pages by comparing current_page with last_page
      const currentPage = response.data.conversations?.current_page || nextPage;
      const lastPage = response.data.conversations?.last_page || currentPage;

      // Update total conversations count
      const total = response.data.conversations?.total || totalConversations;
      setTotalConversations(total);

      // Only show "Load More" if we're not on the last page
      setHasMoreConversations(currentPage < lastPage);
      setPage(nextPage);

    } catch (error) {
      // Error handled silently
    } finally {
      setLoadingMoreConversations(false);
    }
  };

  const startNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setShowSidebar(false);

    // Setze den Fokus auf das Textarea, aber warte bis die UI aktualisiert wurde
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);
  };

  const loadConversation = async (id) => {
    try {
      // Close artifact panel and reset content when switching conversations
      setShowArtifact(false);
      setArtifactContent(null);
      setPrintContent(null);

      // Set conversation ID
      setConversationId(id);

      // Load conversation messages
      const response = await axios.post(`/conversations/${id}?page=1`);

      if (response.data && response.data.messages && response.data.messages.data) {
        setMessages(response.data.messages.data);

        // Use multiple timers with increasing delays to ensure scrolling works
        setTimeout(scrollToBottom, 50);
        setTimeout(scrollToBottom, 150);
        setTimeout(scrollToBottom, 300);
      }

      // Close sidebar on mobile after selecting a conversation
      if (isMobile) {
        setShowSidebar(false);
      }
    } catch (error) {
      // Error handled silently
    }
  };

  const handleSaveConversationName = async (id) => {
    try {
      if (!editedConversationName.trim()) {
        setEditedConversationName('Unbenannte Konversation');
      }

      await axios.put(`/conversations/${id}`, {
        name: editedConversationName.trim() || 'Unbenannte Konversation'
      });

      // Update conversations list
      setConversations(prevConversations =>
        prevConversations.map(conv =>
          conv.conversation_id === id
            ? { ...conv, name: editedConversationName.trim() || 'Unbenannte Konversation' }
            : conv
        )
      );

      setEditingConversationId(null);
    } catch (error) {
      // Error handled silently
    }
  };

  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;

    try {
      await axios.delete(`/conversations/${conversationToDelete.conversation_id}`);

      // Update conversations list
      setConversations(prevConversations =>
        prevConversations.filter(conv => conv.conversation_id !== conversationToDelete.conversation_id)
      );

      // If deleted current conversation, clear it
      if (conversationToDelete.conversation_id === conversationId) {
        setConversationId(null);
        setMessages([]);
      }

      setDeleteModalOpen(false);
      setConversationToDelete(null);
    } catch (error) {
      // Error handled silently
    }
  };

  const handleDeleteAllConversations = async () => {
    // Confirm before deletion
    if (!window.confirm('Möchten Sie wirklich alle Konversationen löschen?')) {
      return;
    }

    try {
      await axios.delete('/conversations/delete-all');

      // Clear all conversations
      setConversations([]);
      setConversationId(null);
      setMessages([]);
    } catch (error) {
      // Error handled silently
    }
  };

  const startDragging = (e) => {
    e.preventDefault();
    setIsDragging(true);
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", stopDragging);

    // Disable text selection during dragging
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  const onDrag = (e) => {
    if (!containerRef.current) return;

    // Get container bounds for precise calculation
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerLeft = containerRect.left;

    // Calculate cursor position relative to container
    const cursorPosition = e.clientX - containerLeft;

    // Calculate new chat width as a percentage of container width with bounds
    let newChatWidth = (cursorPosition / containerWidth) * 100;

    // Apply constraints (min 30%, max 80%)
    if (newChatWidth < 30) newChatWidth = 30;
    if (newChatWidth > 80) newChatWidth = 80;

    // Apply the width change immediately without rounding for smooth movement
    setChatWidth(newChatWidth);
  };

  const stopDragging = () => {
    document.removeEventListener("mousemove", onDrag);
    document.removeEventListener("mouseup", stopDragging);
    setIsDragging(false);

    // Re-enable text selection
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  };

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      // Reset height to calculate scroll height properly - taller when no messages on mobile too
      const defaultHeight = messages.length === 0 ? 48 : (isMobile ? 40 : 48);
      textareaRef.current.style.height = `${defaultHeight}px`;

      // Calculate new height based on content
      const scrollHeight = textareaRef.current.scrollHeight;
      // Apply max height constraint - same max height for both when no messages
      const maxHeight = messages.length === 0 ? 100 : (isMobile ? 80 : 100);
      const newHeight = Math.min(maxHeight, Math.max(defaultHeight, scrollHeight));

      // Apply new height
      textareaRef.current.style.height = `${newHeight}px`;
      setTextareaHeight(newHeight);
    }
  };

  // Function to scroll to bottom of messages
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        // Direct method to scroll to bottom using scrollbar API
        if (chatContainerRef.current) {
          try {
            // Force scroll to the maximum height
            const scrollHeight = chatContainerRef.current.getScrollHeight();
            chatContainerRef.current.scrollTop(scrollHeight);

            // Double-check with another frame to ensure it worked
            requestAnimationFrame(() => {
              if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop(scrollHeight);
              }
            });
          } catch (error) {
            // Error handled silently
          }
        }
      });
    }
  };

  // Auto-scroll when messages change
  useEffect(() => {
    if (messages.length > 0) {
      // Use multiple timers with increasing delays for reliable scrolling
      // This ensures scrolling works even with complex message rendering
      setTimeout(scrollToBottom, 50);
      setTimeout(scrollToBottom, 150);
      setTimeout(scrollToBottom, 300);

      // Extra scroll for when typing animation completes
      if (!typingAnimation && lastReceivedMessage) {
        setTimeout(scrollToBottom, 500);
      }
    }
  }, [messages, typingAnimation]);

  // Fokus auf Textarea setzen, wenn die Seite initial geladen wird und keine Nachrichten vorhanden sind
  useEffect(() => {
    // Nur wenn keine Nachrichten vorhanden sind (d.h. das Textarea ist in der Mitte)
    if (messages.length === 0 && textareaRef.current) {
      // Kurze Verzögerung, um sicherzustellen, dass die UI vollständig geladen ist
      setTimeout(() => {
        textareaRef.current.focus();
      }, 300);
    }
  }, []);  // Leeres Dependency-Array bedeutet: Nur beim ersten Rendern ausführen

  useEffect(() => {
    const handleResize = () => {
      const isSmallScreen = window.innerWidth < 1024;
      setIsMobile(isSmallScreen);

      // Update textarea height based on screen size and message count
      if (textareaRef.current) {
        const defaultHeight = messages.length === 0 ? 48 : (isSmallScreen ? 40 : 48);
        textareaRef.current.style.height = `${defaultHeight}px`;
        setTextareaHeight(defaultHeight);
      }

      if (isSmallScreen) {
        setShowSidebar(false);
        setShowArtifact(false);
      } else {
        // Don't automatically open sidebar for large screens
        // setShowSidebar(true);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize(); // Initialize on mount
    return () => window.removeEventListener("resize", handleResize);
  }, [messages.length]); // Add messages.length as dependency

  // Handle sidebar toggle - synchronize main content animation with CSS
  useEffect(() => {
    // Update main container class based on sidebar state
    if (mainContainerRef.current) {
      if (showSidebar) {
        mainContainerRef.current.classList.add('sidebar-open');
        mainContainerRef.current.classList.remove('sidebar-closed');
      } else {
        mainContainerRef.current.classList.add('sidebar-closed');
        mainContainerRef.current.classList.remove('sidebar-open');
      }
    }
  }, [showSidebar]);

  const calculatedChatWidth = showArtifact ? `${chatWidth}%` : "100%";
  const chatContainerClasses = `flex-1 overflow-y-auto p-4 ${messages.length === 0 ? "flex flex-col justify-center items-center text-center" : ""}`;
  const textareaContainerClasses = `bg-white border-t p-4 w-full ${messages.length === 0 ? "absolute inset-x-0 bottom-1/2 translate-y-1/2" : "fixed inset-x-0 bottom-0 lg:relative"}`;

  // Effect for thinking text animation
  useEffect(() => {
    if (isLoading) {
      const randomIndex = Math.floor(Math.random() * thinkingPhrases.length);
      setThinkingText(thinkingPhrases[randomIndex]);

      const thinkingTextInterval = setInterval(() => {
        const randomIndex = Math.floor(Math.random() * thinkingPhrases.length);
        setThinkingText(thinkingPhrases[randomIndex]);
      }, 3000);

      const dotCountInterval = setInterval(() => {
        setDotCount((prevCount) => (prevCount + 1) % 4);
      }, 200);

      return () => {
        clearInterval(thinkingTextInterval);
        clearInterval(dotCountInterval);
      };
    }
  }, [isLoading]);

  // Handle sending message to OpenAI
  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!textareaContent.trim() || isLoading) return;

    // Store message content and clear input
    const messageText = textareaContent;
    setTextareaContent('');
    // Reset to default height based on device and messages count
    setTextareaHeight(isMobile && messages.length > 0 ? 40 : 48);

    // Add user message to the UI immediately with a temporary flag
    const userMessage = { role: 'user', content: messageText, temp: true };
    setMessages(prev => [...prev, userMessage]);

    // Set loading state
    setIsLoading(true);

    try {
      const payload = { prompt: messageText };

      // Include conversation ID if one exists
      if (conversationId) {
        payload.conversation_id = conversationId;
      }

      // Send message to API
      const response = await axios.post('/chat', payload);

      // Create assistant message
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response
      };
      setLastReceivedMessage(assistantMessage);

      // Update messages, filter out temp messages
      setMessages(prev => prev.filter(msg => !msg.temp).concat([
        { role: 'user', content: messageText },
        assistantMessage
      ]));

      // Set typing animation
      setTypingAnimation(true);

      // Disable animation after delay based on message length
      setTimeout(() => {
        setTypingAnimation(false);
        setIsLoading(false);

        // Refocus the textarea after message is sent and loading is done
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, Math.min(1500, response.data.response.length * 1000 / typingSpeed));

      // Set conversation ID if this is a new conversation
      if (!conversationId && response.data.conversation_id) {
        setConversationId(response.data.conversation_id);
        fetchConversations();
      }
    } catch (error) {
      setIsLoading(false);

      // Remove temporary user message if there was an error
      setMessages(prev => prev.filter(msg => !msg.temp));

      // Display error message
      alert('Fehler beim Senden der Nachricht. Bitte versuche es erneut.');

      // Refocus the textarea even if there was an error
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  };

  // Handler for textarea change
  const handleTextareaChange = (e) => {
    setTextareaContent(e.target.value);

    // We need to ensure adjustTextareaHeight is called with the latest isMobile value
    if (textareaRef.current) {
      // Reset height to calculate scroll height properly - taller when no messages on mobile too
      const defaultHeight = messages.length === 0 ? 48 : (isMobile ? 40 : 48);
      textareaRef.current.style.height = `${defaultHeight}px`;

      // Calculate new height based on content
      const scrollHeight = textareaRef.current.scrollHeight;
      // Apply max height constraint - same max height for both when no messages
      const maxHeight = messages.length === 0 ? 100 : (isMobile ? 80 : 100);
      const newHeight = Math.min(maxHeight, Math.max(defaultHeight, scrollHeight));

      // Apply new height
      textareaRef.current.style.height = `${newHeight}px`;
      setTextareaHeight(newHeight);
    }
  };

  // Enter key handler
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (textareaContent.trim() && !isLoading) {
        handleSendMessage();
      }
    }
  };

  // Update adjustTextareaHeight when textareaContent changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [textareaContent]);

  // Function to handle textarea scrolling
  const handleTextareaScroll = () => {
    if (textareaRef.current && textareaScrollbarRef.current) {
      const scrollPercentage = textareaRef.current.scrollTop /
        (textareaRef.current.scrollHeight - textareaRef.current.clientHeight);

      textareaScrollbarRef.current.scrollTop(
        scrollPercentage *
        (textareaScrollbarRef.current.getScrollHeight() - textareaScrollbarRef.current.getClientHeight())
      );
    }
  };

  useEffect(() => {
    // Listen for typing completion events from ChatMessageAssistant
    const handleTypingComplete = () => {
      // Scroll to bottom when typing animation completes
      scrollToBottom();
    };

    document.addEventListener('typingComplete', handleTypingComplete);
    document.addEventListener('typingUpdate', scrollToBottom);

    return () => {
      document.removeEventListener('typingComplete', handleTypingComplete);
      document.removeEventListener('typingUpdate', scrollToBottom);
    };
  }, []);

  // Function to fetch artifact content
  const fetchArtifactContent = async (refresh = false) => {
    if (!conversationId) {
      return;
    }

    try {
      setIsArtifactLoading(true);
      setArtifactContent(null); // Clear previous content
      setLoadingTime(0); // Reset timer

      // Start timer
      const timer = setInterval(() => {
        setLoadingTime(prev => prev + 1);
      }, 1000);
      setLoadingTimer(timer);

      let response;

      // If refresh is requested, directly generate a new artifact
      if (refresh) {
        response = await axios.post(`/artifact/${conversationId}?refresh=true`);

        // Set the artifact content directly from the response with current timestamp for updated_at
        const regeneratedData = {
          ...response.data,
          created_at: response.data.summary?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString() // Add current date as updated_at
        };
        setArtifactContent(regeneratedData);
      } else {
        // First check if an artifact exists
        const checkResponse = await axios.get(`/artifact/exists/${conversationId}`);

        if (checkResponse.data.exists) {
          // Artifact exists, use it
          // Store both timestamps from the artifact record
          const artifactData = {
            ...checkResponse.data.content,
            created_at: checkResponse.data.created_at,
            updated_at: checkResponse.data.updated_at
          };
          setArtifactContent(artifactData);
        } else {
          // No artifact, generate a new one
          response = await axios.post(`/artifact/${conversationId}`);

          // Set the artifact content with current timestamps
          const newArtifactData = {
            ...response.data,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString() // Add current date as updated_at
          };
          setArtifactContent(newArtifactData);
        }
      }
    } catch (error) {
      // Store a generic error structure
      setArtifactContent({
        success: false,
        error: `Failed to fetch artifact content. Status: ${error.response?.status || 'Network Error'}`
      });
    } finally {
      setIsArtifactLoading(false);
      // Clear timer
      if (loadingTimer) {
        clearInterval(loadingTimer);
        setLoadingTimer(null);
      }
    }
  };

  // Modified toggle artifact function
  const toggleArtifact = async (contentType = 'summary') => {
    // If the artifact is already visible with the same content type, close it
    if (showArtifact && artifactContentType === contentType) {
      setShowArtifact(false);
      return;
    }

    // Otherwise, show artifact with the specified content type
    // Set artifact state and content type
    setArtifactContentType(contentType);
    setShowArtifact(true);

    // If opening the panel, fetch appropriate content
    if (contentType === 'summary') {
      // Fetch summary content
      await fetchArtifactContent(false); // Use cached version if available
    } else if (contentType === 'print') {
      // Generate print content
      await generatePrintContent();
    }
  };

  // Toggle print panel function
  const togglePrintPanel = async () => {
    const willBeVisible = !showPrintPanel;

    // Set print panel state
    setShowPrintPanel(willBeVisible);

    // If opening the panel, generate print content
    if (willBeVisible && conversationId) {
      await generatePrintContent();
    }
  };

  // Function to generate printable content
  const generatePrintContent = async () => {
    if (!conversationId) {
      return;
    }

    try {
      setIsPrintLoading(true);
      setPrintContent(null); // Clear previous content

      // Prepare formatted messages for printing
      const printableMessages = messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.created_at || new Date().toISOString()
      }));

      // Set the print content
      setPrintContent({
        conversationName: conversationId && conversations.find(c => c.conversation_id === conversationId)?.name,
        messages: printableMessages,
        created_at: new Date().toISOString()
      });
    } catch (error) {
      // Store error information
      setPrintContent({
        success: false,
        error: `Failed to generate print content: ${error.message || 'Unknown error'}`
      });
    } finally {
      setIsPrintLoading(false);
    }
  };

  // Replace the internal printConversation function with the imported one
  const handlePrintConversation = () => {
    if (printContent) {
      printConversation(printContent);
    }
  };

  // Replace the internal downloadConversationPdf function with the imported one
  const handleDownloadConversationPdf = () => {
    if (printContent) {
      downloadConversationPdf(printContent);
    }
  };

  // Füge eine useEffect hinzu, um den Benutzer zu laden und auf Klicks außerhalb des Dropdown-Menüs zu achten
  useEffect(() => {
    // Optional: Benutzerinformationen laden
    // fetchUserInfo();

    // Event-Handler für Klick außerhalb des Dropdown-Menüs
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Funktion zum Abmelden
  const handleLogout = async () => {
    try {
      await axios.post('/logout');
      window.location.href = '/login';
    } catch (error) {
      // Error handled silently
    }
  };

  // Component for skeleton loader
  const SkeletonLoader = () => {
    return (
      <div className="skeleton-loader">
        <div className="loading-timer mb-4">
            Generiere Zusammenfassung... <span>{loadingTime}s</span>
        </div>
        <div className="skeleton-container">
          <div className="skeleton-header">
            <div className="skeleton-title skeleton-pulse"></div>
            <div className="skeleton-subtitle skeleton-pulse"></div>
            <div className="skeleton-date skeleton-pulse"></div>
          </div>

          {/* Section 1 */}
          <div className="skeleton-section">
            <div className="skeleton-section-title skeleton-pulse"></div>
            <div className="skeleton-text skeleton-text-full skeleton-pulse"></div>
            <div className="skeleton-text skeleton-text-large skeleton-pulse"></div>
            <div className="skeleton-text skeleton-text-medium skeleton-pulse"></div>
          </div>

          {/* Section 2 */}
          <div className="skeleton-section">
            <div className="skeleton-section-title skeleton-pulse"></div>
            <div className="skeleton-list">
              <div className="skeleton-list-item">
                <div className="skeleton-text skeleton-text-large skeleton-pulse"></div>
              </div>
              <div className="skeleton-list-item">
                <div className="skeleton-text skeleton-text-medium skeleton-pulse"></div>
              </div>
              <div className="skeleton-list-item">
                <div className="skeleton-text skeleton-text-large skeleton-pulse"></div>
              </div>
            </div>
          </div>

          {/* Section 3 */}
          <div className="skeleton-section">
            <div className="skeleton-section-title skeleton-pulse"></div>
            <div className="skeleton-text skeleton-text-full skeleton-pulse"></div>
            <div className="skeleton-text skeleton-text-large skeleton-pulse"></div>
          </div>

        </div>
      </div>
    );
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (loadingTimer) {
        clearInterval(loadingTimer);
      }
    };
  }, [loadingTimer]);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Regular style tag for dynamic styles only */}
      <style>
        {`
        /* Draggable divider styles with dynamic state */
        .divider {
          background-color: ${isDragging ? '#e5e7eb' : '#f3f4f6'};
        }

        .divider-line {
          background-color: ${isDragging ? '#3b82f6' : '#9ca3af'};
        }

        .divider-dots {
          opacity: ${isDragging ? '1' : '0'};
        }
        `}
      </style>

      {/* Use the Sidebar component with conversation props */}
      <Sidebar
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        isMobile={isMobile}
        // Conversation props
        conversations={conversations}
        conversationId={conversationId}
        loadConversation={loadConversation}
        startNewConversation={startNewConversation}
        editingConversationId={editingConversationId}
        setEditingConversationId={setEditingConversationId}
        editedConversationName={editedConversationName}
        setEditedConversationName={setEditedConversationName}
        handleSaveConversationName={handleSaveConversationName}
        setDeleteModalOpen={setDeleteModalOpen}
        setConversationToDelete={setConversationToDelete}
        loadingMoreConversations={loadingMoreConversations}
        loadMoreConversations={loadMoreConversations}
        hasMoreConversations={hasMoreConversations}
        onDeleteAllConversations={handleDeleteAllConversations}
        fetchConversations={fetchConversations}
        setConversations={setConversations}
        totalConversations={totalConversations}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && conversationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium mb-3">Konversation löschen</h3>
            <p className="text-gray-600 mb-4">
              Möchten Sie die Konversation "{conversationToDelete.name || 'Unbenannte Konversation'}" wirklich löschen?
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setConversationToDelete(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteConversation}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <div
        ref={mainContainerRef}
        className={`main-container ${showSidebar ? 'sidebar-open' : 'sidebar-closed'} flex flex-col`}
      >
        <div id="main-header" className={`flex justify-between items-center p-4 ${messages.length !== 0 ? 'border-b shadow-sm ' : ''}`}>
          <div className={`flex items-center ${messages.length === 0 && !showSidebar ? 'flex items-center shadow-lg px-4 py-3 bg-white rounded-lg mt-6' : ''}`}>

            {!showSidebar && (
              <button
                className={isMobile ? "mobile-menu-button" : "mr-3 text-gray-600 hover:text-gray-800 transition-colors"}
                onClick={() => setShowSidebar(true)}
              >
                <HiOutlineBars3 size={20} />
              </button>
            )}
            <div className={`header-container flex items-center`}>
              <a href="/" className={`header-content flex items-center ${showSidebar ? 'hidden' : 'visible'}`}>
                  <img
                      src="/images/logo/leadersoft-client-plattform.svg"
                      alt="Bernie Logo"
                      className="h-6 mr-2"
                  />
              </a>
              <h1 className="text-sm font-normal conversation-name">
                {conversationId && conversations.find(c => c.conversation_id === conversationId)?.name ? ` ${showSidebar ? '' : '- '} ${conversations.find(c => c.conversation_id === conversationId)?.name}` : ""}
              </h1>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {messages.length > 0 && (
              <>
                <button
                  className="artifact-toggle-btn bg-white text-blue-500 border border-blue-300 hover:bg-blue-50 px-2 py-1 rounded flex items-center text-sm transition-colors"
                  onClick={() => toggleArtifact('print')}
                >
                  {showArtifact && artifactContentType === 'print' ? (
                    <FiX size={16} className="toggle-icon" />
                  ) : (
                    <FiPrinter size={16} className="toggle-icon" />
                  )}
                  <span className="toggle-text ml-1 sm:flex hidden">Drucken</span>
                </button>
                <button
                  className="artifact-toggle-btn bg-white text-blue-500 border border-blue-300 hover:bg-blue-50 px-2 py-1 rounded flex items-center text-sm transition-colors"
                  onClick={() => toggleArtifact('summary')}
                >
                  {showArtifact && artifactContentType === 'summary' ? (
                    <FiX size={16} className="toggle-icon" />
                  ) : (
                    <BiNote size={16} className="toggle-icon" />
                  )}
                  <span className="toggle-text ml-1 sm:flex hidden">Resümee</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Chat and Artifact */}
        <div
          className="flex flex-1 overflow-hidden relative"
          ref={containerRef}
        >
          {/* Chat */}
          <div
            className="relative h-full flex flex-col min-w-[400px]"
            style={{ width: calculatedChatWidth }}
          >
            <CustomScrollbars
              ref={chatContainerRef}
              autoHide
              autoHideTimeout={1000}
              autoHideDuration={200}
              className="flex-1"
            >
              {/* CHAT CONTENT */}
                <div className={`p-4 ${messages.length === 0 ? "flex flex-col justify-center items-center text-center h-full" : "max-size-chat mx-auto sm:pb-16 pb-28"}`}>
                    {messages.length > 0 ? (
                        <>
                            {messages.map((msg, idx) => (
                                <div key={idx} className="mb-4">
                                    {msg.role === 'user' ? (
                                        <ChatMessageUser message={msg} />
                                    ) : (
                                        <ChatMessageAssistant
                                            message={msg}
                                            animateTyping={typingAnimation && idx === messages.length - 1}
                                        />
                                    )}
                                </div>
                            ))}
                            {isLoading && !typingAnimation && (
                                <div className="flex justify-start my-2">
                                    <div className="text-base text-gray-500 italic pl-2 flex gap-2">
                                        <BernieLoading lineWidth={16} minLineWidth={24} maxLineWidth={24} />
                                        {thinkingText}{'.'.repeat(Math.min(3, dotCount))}
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        ''
                    )}
                </div>
            </CustomScrollbars>

            {/* Textarea with improved scrolling and centering */}
            <div className={` w-full  justify-center ${messages.length === 0 ? "absolute inset-x-0 bottom-1/2 translate-y-1/2" : "footer-text-container flex py-4 fixed inset-x-0 bottom-0 lg:relative"}`}>

                {messages.length === 0 ? (
                    <div className={`p-4 ${messages.length === 0 ? "flex flex-col justify-center items-center text-center h-full" : ""}`}>
                    <h3 className="sm:text-3xl text-xl font-medium mb-2" style={{ 'color':'#204256' }}>Beschreibe dein Problem</h3>
                    </div>
                ) : ( '' )}
              <div className="w-full mx-auto sm:px-4 px-4">
                <div
                  className="relative border focus-within:border-gray-300 transition-colors mx-auto max-size-chat"
                  style={{
                    width: '100%',
                    borderRadius: '24px',
                  }}
                >
                  <CustomScrollbars
                    ref={textareaScrollbarRef}
                    autoHide
                    autoHideTimeout={1000}
                    autoHideDuration={200}
                    style={{
                      height: textareaHeight,
                      maxHeight: messages.length === 0 ? '100px' : (isMobile ? '80px' : '100px')
                    }}
                    universal={true}
                    renderThumbVertical={({ style, ...props }) => (
                      <div
                        {...props}
                        style={{
                          ...style,
                          backgroundColor: '#cbd5e1',
                          borderRadius: '24px',
                          width: '6px',
                        }}
                      />
                    )}
                  >
                    <textarea
                      ref={textareaRef}
                      className="w-full resize-none p-3 pr-10"
                      style={{
                        minHeight: messages.length === 0 ? '48px' : (isMobile ? '40px' : '48px'),
                        height: `${textareaHeight}px`,
                        border: 'none',
                        outline: 'none',
                        display: 'block',
                        borderRadius: '24px',
                        background: 'rgb(238, 240, 246)',
                        boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 1px 0px inset, rgba(0, 0, 0, 0.05) 1px 0px 1px 0px inset, rgba(0, 0, 0, 0.05) -1px 0px 1px 0px inset'
                      }}
                      placeholder={messages.length === 0 ? 'Beschreibe mir woran du bist und was dein Problem ist..' : 'Nachricht eingeben...'}
                      value={textareaContent}
                      onChange={handleTextareaChange}
                      onKeyDown={handleKeyDown}
                      disabled={isLoading}
                    />
                  </CustomScrollbars>

                  <button
                    className="secondary-3d absolute right-3 bottom-3 text-blue-500 z-10"
                    onClick={handleSendMessage}
                    disabled={isLoading || !textareaContent.trim()}
                  >
                    {isLoading ? (
                      <AiOutlineLoading3Quarters className="animate-spin h-5 w-5 text-blue-800" />
                    ) : (
                      <LuSendHorizontal size={20} />
                    )}
                  </button>
                </div>
                <div className="mt-1 text-center text-xs text-gray-400">
                    Mit der Nutzung stimmen Sie den <a href="https://quantonomics.com/datenschutzerklaerung/" target='_blank' className="hover:text-blue-600 underline">Bedingungen</a> zu.
                </div>
              </div>
            </div>
          </div>

          {/* Draggable Divider */}
          {showArtifact && (
            <div
              ref={dragRef}
              onMouseDown={startDragging}
              className={`divider ${isDragging ? 'dragging' : ''}`}
            >
              <div className="divider-line"></div>
              <div className="divider-dots">
                <div className="divider-dot"></div>
                <div className="divider-dot"></div>
                <div className="divider-dot"></div>
              </div>
            </div>
          )}

          {/* Unified Artifact Panel */}
          {showArtifact && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col border-l bg-gray-50 artifact-full-width"
              style={{ backgroundColor: '#f5f5f7' }}
            >
              <div className="flex justify-between items-center p-4 border-b bg-white shadow-sm">
                <div className="text-lg font-semibold flex items-center">
                  {artifactContentType === 'summary' ? (
                    <>
                      <BiNote size={20} className="mr-2 text-blue-600" />
                      <span className="artifact-title text-gray-800">Zusammenfassung</span>
                    </>
                  ) : (
                    <>
                      <FiPrinter size={20} className="mr-2 text-blue-600" />
                      <span className="artifact-title text-gray-800">Konversation drucken</span>
                    </>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    className="text-gray-500 hover:text-blue-500 p-1"
                    title="Aktualisieren"
                    onClick={() => artifactContentType === 'summary'
                      ? fetchArtifactContent(true)
                      : generatePrintContent()}
                    disabled={artifactContentType === 'summary' ? isArtifactLoading : isPrintLoading}
                  >
                    {(artifactContentType === 'summary' && isArtifactLoading) ||
                     (artifactContentType === 'print' && isPrintLoading) ? (
                      <AiOutlineLoading3Quarters className="animate-spin h-5 w-5" />
                    ) : (
                      <FiRefreshCw size={18} />
                    )}
                  </button>
                  <button
                    className="text-gray-500 hover:text-blue-500 p-1"
                    title="Drucken"
                    onClick={artifactContentType === 'summary'
                      ? printArtifact({ documentTitle: conversationId && conversations.find(c => c.conversation_id === conversationId)?.name })
                      : handlePrintConversation}
                    disabled={(artifactContentType === 'print' && (!printContent || isPrintLoading))}
                  >
                    <FiPrinter size={18} />
                  </button>
                  <button
                    className="text-gray-500 hover:text-blue-500 p-1"
                    title="Herunterladen"
                    onClick={artifactContentType === 'summary'
                      ? generatePdf({ documentTitle: conversationId && conversations.find(c => c.conversation_id === conversationId)?.name })
                      : handleDownloadConversationPdf}
                    disabled={(artifactContentType === 'print' && (!printContent || isPrintLoading))}
                  >
                    <FiDownload size={18} />
                  </button>
                  <button
                    className="close-button"
                    title="Schließen"
                    onClick={() => setShowArtifact(false)}
                  >
                    <FiX size={16} />
                  </button>
                </div>
              </div>

              <CustomScrollbars
                autoHide
                autoHideTimeout={1000}
                autoHideDuration={200}
                className="flex-1 bg-white"
                ref={artifactContentType === 'summary' ? artifactRef : null}
              >
                {/* Conditional rendering based on content type */}
                {artifactContentType === 'summary' ? (
                  <div className="p-0 h-full">
                    {isArtifactLoading ? (
                      <SkeletonLoader />
                    ) : artifactContent ? (
                      <div className="h-full">
                        {artifactContent.success === false ? (
                          <div className="bg-red-50 p-4 rounded-lg text-red-800 m-4">
                            <h3 className="font-bold mb-2">Fehler beim Generieren des Artifacts</h3>
                            <p>{artifactContent.error || "Unbekannter Fehler ist aufgetreten"}</p>
                          </div>
                        ) : (
                          <div className="apple-doc h-full">
                            <div className="apple-doc-header">
                              {conversationId && conversations.find(c => c.conversation_id === conversationId)?.name && (
                                <h2 className="apple-doc-title">{conversations.find(c => c.conversation_id === conversationId)?.name}</h2>
                              )}
                              <p className="apple-doc-subtitle">Führungsanalyse mit Leadersoft.ai</p>
                              {artifactContent.created_at && (
                                <small className="apple-doc-date text-right">{new Date(artifactContent.created_at).toLocaleDateString('de-CH')}</small>
                              )}
                            </div>

                            {artifactContent.summary ? (
                              <div>
                                {Object.entries(artifactContent.summary).map(([key, value], idx) => {
                                  // Skip created_at as it's already shown
                                  // Skip rendering if value is empty, null, undefined or empty array/object
                                  if (key === 'created_at' ||
                                      !value ||
                                      (Array.isArray(value) && value.length === 0) ||
                                      (typeof value === 'object' && Object.keys(value).length === 0)) {
                                    return null;
                                  }

                                  // Map keys to German section titles
                                  const sectionTitles = {
                                    'situation_overview': 'Situationsübersicht',
                                    'options_considered': 'Handlungsoptionen',
                                    'selected_approach': 'Gewählter Ansatz',
                                    'implementation_plan': 'Umsetzungsplan',
                                    'follow_up': 'Nachverfolgung',
                                    'reflection_questions': 'Reflexionsfragen'
                                  };

                                  // Check if there is valid content to display
                                  let hasValidContent = false;

                                  // For arrays of objects like options_considered
                                  if (key === 'options_considered' && Array.isArray(value) && value.length > 0) {
                                    hasValidContent = true;
                                  }
                                  // For object types like selected_approach
                                  else if (key === 'selected_approach' && typeof value === 'object' && value.name) {
                                    hasValidContent = true;
                                  }
                                  // For simple arrays of strings
                                  else if (Array.isArray(value) && value.length > 0) {
                                    hasValidContent = true;
                                  }
                                  // For objects with sub-values
                                  else if (typeof value === 'object' && !Array.isArray(value)) {
                                    // Check if any of the object's properties has valid content
                                    for (const [subKey, subValue] of Object.entries(value)) {
                                      if (subValue &&
                                          !(Array.isArray(subValue) && subValue.length === 0) &&
                                          !(typeof subValue === 'object' && Object.keys(subValue).length === 0)) {
                                        hasValidContent = true;
                                        break;
                                      }
                                    }
                                  }
                                  // For simple string/number values
                                  else if (value && typeof value !== 'object') {
                                    hasValidContent = true;
                                  }

                                  // Only render the section if it has valid content
                                  if (!hasValidContent) {
                                    return null;
                                  }

                                  return (
                                    <div key={idx} className="apple-doc-section">
                                      <h3 className="apple-doc-section-title">{sectionTitles[key] || key.replace(/_/g, ' ')}</h3>

                                      {key === 'options_considered' && Array.isArray(value) ? (
                                        <>
                                          {value.map((option, optionIdx) => {
                                            let parsedOption = option;
                                            // Try to parse JSON string if it's a string
                                            if (typeof option === 'string') {
                                              try {
                                                parsedOption = JSON.parse(option);
                                              } catch (e) {
                                                // If parsing fails, keep original
                                              }
                                            }

                                            return (
                                              <div key={optionIdx} className="apple-doc-subsection">
                                                <h4 className="apple-doc-subsection-title">{optionIdx + 1}. {parsedOption.name || 'Option'}</h4>
                                                <p className="apple-doc-text">{parsedOption.description}</p>

                                                {/* Benefits and Risks container - flex row on larger screens, column on smaller */}
                                                <div className="benefits-risks-container">
                                                  {parsedOption.benefits && parsedOption.benefits.length > 0 && (
                                                    <div className="option-details benefits-section">
                                                      <h5 className="detail-heading">Vorteile</h5>
                                                      <ul className="list-disc">
                                                        {parsedOption.benefits.map((benefit, i) => (
                                                          <li key={i} className="apple-doc-text">{benefit}</li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}

                                                  {parsedOption.challenges && parsedOption.challenges.length > 0 && (
                                                    <div className="option-details risks-section">
                                                      <h5 className="detail-heading">Risiken</h5>
                                                      <ul className="list-disc">
                                                        {parsedOption.challenges.map((challenge, i) => (
                                                          <li key={i} className="apple-doc-text">{challenge}</li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </>
                                      ) : key === 'selected_approach' && typeof value === 'object' ? (
                                        <div className="apple-doc-subsection">
                                          <h4 className="apple-doc-subsection-title">{value.name}</h4>
                                          {value.rationale && <p className="apple-doc-text">{value.rationale}</p>}
                                        </div>
                                      ) : Array.isArray(value) ? (
                                        <ul className="list-disc">
                                          {value.map((item, i) => (
                                            <li key={i} className="apple-doc-text">
                                              {typeof item === 'object' ? (
                                                <>
                                                  {item.step && <strong>{item.step}</strong>}
                                                  {item.step && item.timeline && ': '}
                                                  {item.timeline && <span>{item.timeline}</span>}
                                                  {(!item.step && !item.timeline) && JSON.stringify(item)}
                                                </>
                                              ) : (
                                                item
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : typeof value === 'object' && !Array.isArray(value) ? (
                                        Object.entries(value).map(([subKey, subValue], subIdx) => {
                                          // Skip rendering if subValue is empty
                                          if (!subValue ||
                                              (Array.isArray(subValue) && subValue.length === 0) ||
                                              (typeof subValue === 'object' && Object.keys(subValue).length === 0)) {
                                            return null;
                                          }

                                          return (
                                            <div key={`${idx}-${subIdx}`} className="apple-doc-subsection">
                                              <h4 className="apple-doc-subsection-title">{subKey.replace(/_/g, ' ')}</h4>
                                              <p className="apple-doc-text">
                                                {typeof subValue === 'string' ? subValue : JSON.stringify(subValue)}
                                              </p>
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <p className="apple-doc-text">{value}</p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="apple-doc-text">Kein Inhalt verfügbar</p>
                            )}

                            <div className="apple-doc-footer">
                              Bernie's AI by Quantonomics
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-gray-500 text-center">Kein Inhalt verfügbar</div>
                    )}
                  </div>
                ) : (
                  <div className='p-4'>
                      <ConversationPrint
                        isLoading={isPrintLoading}
                        content={printContent}
                        onRefresh={generatePrintContent}
                      />
                  </div>
                )}
              </CustomScrollbars>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChatLayout;
